import { extend } from 'flarum/common/extend';
import app from 'flarum/common/app';
import Application from 'flarum/common/Application';
import DiscussionHero from 'flarum/forum/components/DiscussionHero';
import DiscussionListItem from 'flarum/forum/components/DiscussionListItem';
import sortTags from 'flarum/tags/utils/sortTags';
import isDark from 'flarum/common/utils/isDark';

// Disable CSS transitions for 2 frames so color vars snap instantly.
function suppressTransitions(): void {
  document.documentElement.classList.add('colored--instant');
  requestAnimationFrame(() => requestAnimationFrame(() => {
    document.documentElement.classList.remove('colored--instant');
  }));
}

// Track last applied color so we only suppress transitions on actual color changes,
// not on every Mithril redraw (which would kill search/other animations mid-flight).
let lastAppliedColor: string | null | undefined = undefined;

function applyColor(color: string | null | undefined): void {
  if (color === lastAppliedColor) return;
  lastAppliedColor = color;
  suppressTransitions();
  if (color) {
    const contrast = isDark(color) ? 'var(--text-on-dark)' : 'var(--text-on-light)';
    document.body.style.setProperty('--colored-color', color);
    document.body.style.setProperty('--colored-contrast', contrast);
    document.body.classList.add('colored--active');
  } else {
    document.body.style.removeProperty('--colored-color');
    document.body.style.removeProperty('--colored-contrast');
    document.body.classList.remove('colored--active');
  }
}

function clearColor(): void {
  applyColor(null);
}

app.initializers.add('ramon-colored', () => {
  // In Flarum 1.x, app.forum is populated AFTER initializers run (in boot(), right
  // before mount()). So defer attribute reads until mount() via an extend hook.
  extend(Application.prototype, 'mount', function () {
    const borderStyle = app.forum.attribute<string>('colored.borderStyle') || 'none';
    document.documentElement.setAttribute('data-colored-border', borderStyle);
  });

  // Patch DiscussionListItem.view:
  //   1. inject --item-tag-color for border LESS rules
  //   2. call applyColor on click so colors swap before the discussion page loads
  extend(DiscussionListItem.prototype, 'view', function (vdom) {
    const tags = sortTags((this.attrs.discussion?.tags() as any[]) || []);
    const color: string | null = tags.length ? (tags[0] as any).color() : null;
    if (!color || !vdom?.attrs) return;

    vdom.attrs.style = { ...(vdom.attrs.style || {}), '--item-tag-color': color };

    const prev = vdom.attrs.onclick;
    vdom.attrs.onclick = (e: MouseEvent) => {
      applyColor(color);
      if (typeof prev === 'function') (prev as Function).call(this, e);
    };
  });

  // Apply color before the first DOM paint of the discussion hero
  extend(DiscussionHero.prototype, ['oninit', 'onupdate'], function () {
    const tags = sortTags((this.attrs.discussion?.tags() as any[]) || []);
    applyColor(tags.length ? (tags[0] as any).color() : null);
  });

  // Restore default colors when leaving the discussion page
  extend(DiscussionHero.prototype, 'onremove', function () {
    clearColor();
  });
});
